<template>
  <a-card class="mt-2 custom-card">
    <a-row type="flex" justify="space-between" align="middle">
      <a-typography-title :level="4" class="text-gray-8 mb-0">
        {{ title }}
      </a-typography-title>
    </a-row>
    <a-row class="mt-3">
      <a-col :span="24">
        <a-table
          class="m-0 p-0"
          :data-source="tickets"
          :columns="columns"
          :scroll="{ x: 1000 }"
          :loading="fetchingTickets"
          :pagination="{
            current: pagination.page,
            pageSize: pagination.items_per_page,
            total: pagination.total_items
          }"
          @change="handleTableChange"
        >
          <template #tag="{ text: tag }">
            <tags :stateId="tag" />
          </template>

          <template #filterDropdown="{ column, confirm, clearFilters }">
            <div style="padding: 8px">
              <a-form
                name="form"
                layout="vertical"
                ref="formRef"
                :model="filterValues"
              >
                <a-form-item :name="column.dataIndex" has-feedback>
                  <a-input
                    v-model:value="filterValues[column.dataIndex]"
                    :placeholder="`Buscar por ${column.title}`"
                    @pressEnter="
                      handleSearch(
                        column.dataIndex,
                        filterValues[column.dataIndex],
                        confirm
                      )
                    "
                    style="width: 188px; display: block"
                  />
                </a-form-item>

                <a-button
                  type="primary"
                  size="small"
                  style="width: 90px; margin-right: 8px"
                  @click="
                    handleSearch(
                      column.dataIndex,
                      filterValues[column.dataIndex],
                      confirm
                    )
                  "
                >
                  Buscar
                </a-button>

                <a-button
                  size="small"
                  style="width: 90px"
                  @click="handleReset(column.dataIndex, clearFilters)"
                >
                  Limpiar
                </a-button>
              </a-form>
            </div>
          </template>

          <template #filterIcon="{ column }">
            <search-outlined
              :style="{
                color: filterValues[column.dataIndex] ? '#2521E5' : undefined
              }"
            />
          </template>

          <template #filterStates="{ column, confirm, clearFilters }">
            <div style="padding: 8px; width: 300px">
              <a-checkbox-group v-model:value="statesSelection">
                <a-row>
                  <a-col
                    :span="24"
                    v-for="(state, index) in statesOptions"
                    :key="index"
                  >
                    <a-checkbox :value="state.value">
                      {{ state.label }}
                    </a-checkbox>
                  </a-col>
                </a-row>
              </a-checkbox-group>

              <div
                class="mt-2 pt-2"
                style="
                  border-top: 1px solid #f0f0f0;
                  display: flex;
                  justify-content: space-between;
                "
              >
                <a-button
                  type="primary"
                  size="small"
                  style="width: 90px; margin-right: 8px"
                  @click="
                    handleSearch(
                      column.dataIndex,
                      statesSelection.join(','),
                      confirm
                    )
                  "
                >
                  Buscar
                </a-button>

                <a-button
                  size="small"
                  style="width: 90px"
                  @click="handleReset(column.dataIndex, clearFilters)"
                >
                  Limpiar
                </a-button>
              </div>
            </div>
          </template>

          <template #filterPendingCases="{ column, confirm, clearFilters }">
            <div style="padding: 8px; width: 300px">
              <a-checkbox-group v-model:value="pendingCasesSelection">
                <a-row>
                  <a-col
                    :span="24"
                    v-for="(pendingCase, index) in pendingCasesOptions"
                    :key="index"
                  >
                    <a-checkbox :value="pendingCase.value">
                      {{ pendingCase.label }}
                    </a-checkbox>
                  </a-col>
                </a-row>
              </a-checkbox-group>

              <div
                class="mt-2 pt-2"
                style="
                  border-top: 1px solid #f0f0f0;
                  display: flex;
                  justify-content: space-between;
                "
              >
                <a-button
                  type="primary"
                  size="small"
                  style="width: 90px; margin-right: 8px"
                  @click="
                    handleSearch(
                      column.dataIndex,
                      pendingCasesSelection.join(','),
                      confirm
                    )
                  "
                >
                  Buscar
                </a-button>

                <a-button
                  size="small"
                  style="width: 90px"
                  @click="handleReset(column.dataIndex, clearFilters)"
                >
                  Limpiar
                </a-button>
              </div>
            </div>
          </template>

          <template #filterIconState="{ column }">
            <filter-outlined
              :style="{
                color:
                  filterValues[column.dataIndex] &&
                  filterValues[column.dataIndex] !== ''
                    ? '#2521E5'
                    : undefined
              }"
            />
          </template>

          <template #action="{ text }">
            <router-link
              v-if="getDetailRoute(text)"
              :to="getDetailRoute(text)"
            >
              Ver
            </router-link>
            <span v-else>-</span>
          </template>
        </a-table>
      </a-col>
    </a-row>
  </a-card>
</template>

<script>
import { onMounted, reactive, ref, toRefs, computed, watch, toRaw } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import moment from 'moment';
import {
  GET_LOGGED_USER,
  TICKET_GROUPS_GET_TICKETS,
  TICKET_GROUPS_GET_TICKETS_PAGINATION,
  TICKET_GROUPS_GET_LOADING
} from '../../store/types';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons-vue';
import getTagColor from '../../utils/getTagColor';
import { getTicketStates } from '@/network';
import Tags from '../tags.vue';
import rutHelper from '@/utils/Ruts';

export default {
  name: 'ListaTicketsMasivos',
  components: {
    tags: Tags,
    'search-outlined': SearchOutlined,
    'filter-outlined': FilterOutlined
  },
  props: {
    title: '',
    defaultRedirect: ''
  },
  setup(props, { emit }) {
    const store = useStore();
    const userRole = store.getters[GET_LOGGED_USER].roleName;
    const columns = [
      {
        width: 100,
        title: 'Id',
        dataIndex: 'id',
        key: 'id'
      },
      {
        width: 150,
        title: 'Póliza',
        dataIndex: 'policies',
        key: 'policies',
        slots: {
          filterDropdown: 'filterDropdown',
          filterIcon: 'filterIcon',
          customRender: 'customRender'
        },
        onFilter: (value, record) =>
          record.group.toString().toLowerCase().includes(value.toLowerCase())
      },
      {
        width: 175,
        title: 'Grupo',
        dataIndex: 'group',
        key: 'group',
        slots: {
          filterDropdown: 'filterDropdown',
          filterIcon: 'filterIcon',
          customRender: 'customRender'
        },
        onFilter: (value, record) =>
          record.group.toString().toLowerCase().includes(value.toLowerCase())
      },
      {
        width: 175,
        title: 'Nombre',
        dataIndex: 'employeeName',
        key: 'employeeName',
        slots: {
          filterDropdown: 'filterDropdown',
          filterIcon: 'filterIcon',
          customRender: 'customRender'
        },
        onFilter: (value, record) =>
          record.employeeName
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
      },
      {
        width: 150,
        title: 'RUT',
        dataIndex: 'employeeRut',
        key: 'employeeRut',
        slots: {
          filterDropdown: 'filterDropdown',
          filterIcon: 'filterIcon',
          customRender: 'customRender'
        },
        onFilter: (value, record) =>
          record.employeeRut
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
      },
      {
        width: 175,
        title: 'Estado',
        dataIndex: 'ticketState',
        key: 'ticketState',
        slots: {
          customRender: 'tag',
          filterDropdown: 'filterStates',
          filterIcon: 'filterIconState'
        },
        onFilter: (value, record) => {
          return statesSelection.value.length === 0
            ? true
            : statesSelection.value.includes(record.ticketState);
        }
      },
      {
        width: 175,
        title: 'Fecha de estado',
        dataIndex: 'stateDate',
        key: 'stateDate'
      },
      {
        width: 150,
        title: 'Casos pendientes',
        dataIndex: 'pendingCases',
        key: 'pendingCases',
        slots: {
          filterDropdown: 'filterPendingCases',
          filterIcon: 'filterIcon',
          customRender: 'customRender'
        },
        onFilter: (value, record) => {
          return pendingCasesSelection.value.some((selection) => {
            if (selection === true) return record.pendingCases > 0;
            if (selection === false) return record.pendingCases === 0;
            return false;
          });
        }
      }
    ];
    const filterValues = ref({});
    const formRef = ref(null);

    const tickets = computed(() => {
      const rawTickets = store.getters[TICKET_GROUPS_GET_TICKETS] || [];
      return Array.isArray(rawTickets)
        ? rawTickets.map((a) => ({
            key: a.id,
            id: a.id,
            policies: a.policies?.join(', ') ?? '',
            group: a.groupCompanies?.join(', ') ?? '',
            employeeName: a.employee?.name?.trim() ?? '',
            employeeRut: a.employee?.rut
              ? rutHelper.formatRut(a.employee.rut)
              : '',
            ticketState: a.state?.id ?? '',
            stateDate: a.state?.stateDate
              ? moment(a.state.stateDate).format('DD/MM/YYYY')
              : '',
            pendingCases: a.pendingCases ?? ''
          }))
        : [];
    });
    watch(
      () => tickets.value,
      () => {
        emit('set-tickets-data', tickets.value);
      }
    );
    const pagination = computed(() => {
      return (
        store.getters[TICKET_GROUPS_GET_TICKETS_PAGINATION] || {
          page: 1,
          items_per_page: 10,
          total_items: 0
        }
      );
    });

    const fetchingTickets = computed(() => {
      return store.getters[TICKET_GROUPS_GET_LOADING].fetchTicketGroupTickets;
    });

    const pendingCasesSelection = ref([]);
    const statesSelection = ref([]);
    const filteredStates = ref([]);
    const pendingCasesOptions = [
      {
        label: 'Sí',
        value: true
      },
      {
        label: 'No',
        value: false
      }
    ];
    const statesOptions = computed(() => {
      return filteredStates.value.map((state) => ({
        label: state.fullTag || state.tag,
        value: state.id
      }));
    });

    if (userRole != 'rrhh') {
      columns.push({
        width: 90,
        title: 'Detalle',
        dataIndex: 'id',
        fixed: 'right',
        align: 'center',
        key: 'id',
        slots: {
          customRender: 'action'
        }
      });
    }

    const state = reactive({
      searchText: '',
      searchedColumn: ''
    });
    const searchInput = ref();

    const handleSearch = (dataIndex, value, confirm) => {
      filterValues.value[dataIndex] = value;
      confirm();

      emit('table-change', {
        pagination: { ...pagination },
        filters: { ...toRaw(filterValues.value) }
      });
    };

    const handleReset = (dataIndex, clearFilters) => {
      if (dataIndex === 'ticketState') {
        statesSelection.value = [];
      }
      if (dataIndex === 'pendingCases') {
        pendingCasesSelection.value = [];
      }
      delete filterValues.value[dataIndex];
      clearFilters();

      emit('table-change', {
        pagination: { ...pagination },
        filters: { ...toRaw(filterValues.value) }
      });
    };

    const handleTableChange = (pag, filters) => {
      const params = {
        pagination: {
          current: pag.current,
          pageSize: pag.pageSize
        },
        filters: { ...toRaw(filterValues.value) }
      };

      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          params.filters[key] = filters[key];
        }
      });

      emit('table-change', params);
    };

    const route = useRoute();
    const detailRouteNames = {
      bajamasivadetail: 'BajaDetail',
      altamasivadetail: 'AltasDetalle',
      altacargasmasivadetalle: 'AltaCargasDetalle',
      bajacargasmasivadetalle: 'BajaCargasDetalle'
    };
    const getDetailRoute = (ticketId) => {
      const routeName =
        detailRouteNames[String(route.name || '').toLowerCase()];

      return routeName
        ? {
            name: routeName,
            params: { id: ticketId }
          }
        : null;
    };

    onMounted(() => {
      getTicketStates().then((response) => {
        filteredStates.value = response.data.filter((s) =>
          [1, 7, 8, 3, 4].includes(s.id)
        );
      });
    });

    return {
      ...toRefs(state),
      getDetailRoute,
      columns,
      searchInput,
      handleSearch,
      handleReset,
      getTagColor,
      filterValues,
      formRef,
      statesOptions,
      statesSelection,
      pagination,
      handleTableChange,
      tickets,
      fetchingTickets,
      pendingCasesOptions,
      pendingCasesSelection
    };
  }
};
</script>

<style scoped>
.custom-card :deep(.ant-table-wrapper .ant-table-pagination) {
  margin-bottom: 0 !important;
}
</style>
